"use client";

import { getGradientAvatarUrl, useAppAccess, useAuth } from "@pex/auth/client";
import { AppContext, type AppContextType } from "@pex/types";
import { ChevronsUpDown, CreditCard, LogOut, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getAppConfigs } from "../../lib/app-config";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { AppSwitcher } from "./app-switcher";

export interface NavDrawerAuthFooterProps {
  accountsLoginUrl?: string;
  avatarUrl?: string | null;
  billingUrl?: string;
  className?: string;
  currentApp?: string;
  requiresAuth?: boolean;
}

export function NavDrawerAuthFooter({
  requiresAuth = false,
  accountsLoginUrl = `${process.env.NEXT_PUBLIC_ACCOUNTS_URL ?? ""}/login`,
  avatarUrl,
  billingUrl,
  className,
  currentApp,
}: NavDrawerAuthFooterProps) {
  const { user, loading, signOut } = useAuth();
  const { userApps, loading: appsLoading } = useAppAccess();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  // Mirrors auth-nav-controls: Website + Finder always show when signed in;
  // conditional apps gated on userApps once useAppAccess resolves.
  const accessibleApps = useMemo(() => {
    if (!user) {
      return [];
    }
    const appNameToContext: Record<string, AppContextType> = {
      Bridge: AppContext.BRIDGE,
      Pulse: AppContext.PULSE,
      Circle: AppContext.CIRCLE,
      Habitat: AppContext.HABITAT,
    };
    return getAppConfigs(currentApp).filter((app) => {
      const appContext = appNameToContext[app.name];
      // Apps with no AppContext (Website, Finder) are public — always show when logged in
      if (!appContext) {
        return true;
      }
      return !appsLoading && userApps.includes(appContext);
    });
  }, [user, userApps, appsLoading, currentApp]);

  const handleSignIn = () => {
    window.location.href = accountsLoginUrl;
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      if (requiresAuth) {
        window.location.href = accountsLoginUrl;
      }
    } catch (error) {
      // Surface the underlying error and un-stick the UI either way. The auth
      // provider clears local state inside signOut() even on error, so the
      // user is effectively signed out even when the upstream call to
      // invalidate the refresh token fails.
      console.error("Sign out failed:", error);
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleManageAccount = () => {
    const accountsUrl = process.env.NEXT_PUBLIC_ACCOUNTS_URL ?? "";
    window.location.href = `${accountsUrl}/manage`;
  };

  const handleBilling = () => {
    if (billingUrl) {
      window.location.href = billingUrl;
    }
  };

  if (!mounted || loading || isSigningOut) {
    return (
      <div className={`flex items-center gap-3 ${className ?? ""}`}>
        <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-muted" />
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          <div className="h-3 w-32 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={className}>
        <Button className="w-full" onClick={handleSignIn}>
          Sign In
        </Button>
      </div>
    );
  }

  const displayName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(" ");
  const avatarSrc =
    avatarUrl ?? user.avatar_url ?? getGradientAvatarUrl(user.email);

  return (
    <div className={`flex items-center gap-2 ${className ?? ""}`}>
      <DropdownMenu
        onOpenChange={(open) => {
          setIsDropdownOpen(open);
          if (open) {
            setIsSwitcherOpen(false);
          }
        }}
        open={isDropdownOpen}
      >
        <DropdownMenuTrigger asChild>
          <button
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg px-1 py-1 text-left hover:bg-accent"
            type="button"
          >
            <Avatar className="shrink-0">
              <AvatarImage alt={displayName || user.email} src={avatarSrc} />
              <AvatarFallback className="animate-pulse" />
            </Avatar>
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium text-sm leading-tight">
                  {displayName || user.email}
                </span>
                <span className="truncate text-muted-foreground text-xs leading-tight">
                  {user.email}
                </span>
              </div>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={handleManageAccount}
          >
            <User className="mr-2 h-4 w-4" />
            My account
          </DropdownMenuItem>
          {billingUrl && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleBilling}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Billing
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={handleSignOut}
            variant="destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {accessibleApps.length > 0 && (
        <AppSwitcher
          apps={accessibleApps}
          className="shrink-0"
          onOpenChange={(open) => {
            setIsSwitcherOpen(open);
            if (open) {
              setIsDropdownOpen(false);
            }
          }}
          open={isSwitcherOpen}
        />
      )}
    </div>
  );
}
